// lib/viewingSettings.ts — a flat's viewing availability and its free slots (docs/plans/viewing-first-onboarding.md,
// phase 2): GET / PUT /units/:id/viewing-settings and GET /units/:id/viewing-slots (Europe/London).
import { api } from './api';
import { londonParts } from './dates';

export const VIEWING_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export type ViewingDay = (typeof VIEWING_DAYS)[number];
export const DAY_LABELS: Record<ViewingDay, string> = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };
export const SLOT_LENGTHS = [15, 30, 45, 60] as const;
/** Start / end choices: every half hour from 07:00 to 21:00. */
export const HALF_HOURS = Array.from({ length: 29 }, (_, i) => `${String(7 + Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);

export interface UnitViewingSettings {
  unitId: string;
  days: ViewingDay[];
  startTime: string;
  endTime: string;
  slotMinutes: number;
  capacity: number;
  daysOff: string[];
  /** Nothing saved yet: these are the defaults (Mon–Fri, 09:00–17:00, 30 minutes, 1 per slot). */
  isDefault: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}
export type ViewingSettingsInput = Pick<UnitViewingSettings, 'days' | 'startTime' | 'endTime' | 'slotMinutes' | 'capacity' | 'daysOff'>;
export interface FreeSlot { startsAt: string; endsAt: string; remaining: number }

export const viewingSettingsApi = {
  get: async (unitId: string) => (await api.get<UnitViewingSettings>(`/units/${unitId}/viewing-settings`)).data,
  put: async (unitId: string, body: ViewingSettingsInput) => (await api.put<UnitViewingSettings>(`/units/${unitId}/viewing-settings`, body)).data,
  /** The free slots between two London days (inclusive; at most 8 weeks). */
  slots: async (unitId: string, q: { from?: string; to?: string } = {}) => (await api.get<FreeSlot[]>(`/units/${unitId}/viewing-slots`, q)).data,
};

/** Free slots grouped by London day, each with its London start time ('HH:MM'). */
export function slotsByDay(slots: FreeSlot[]) {
  const days = new Map<string, Array<FreeSlot & { time: string }>>();
  for (const s of slots) {
    const { day, time } = londonParts(s.startsAt);
    days.set(day, [...(days.get(day) ?? []), { ...s, time }]);
  }
  return days;
}
